import { useState, useMemo, useEffect } from "react";
import { 
  X, Calendar as CalendarIcon, TrendingUp, TrendingDown, 
  Clock, ArrowUpRight, ArrowDownRight, Wallet,
  Award, BarChart3, ExternalLink, Copy, Check
} from "lucide-react";
import { formatUsdCompact } from "@/lib/formatUsd";
import { TokenAvatar } from "@/components/TokenAvatar";

export interface DayDataPayload {
  dateKey: string;
  date: Date;
  pnlSol: number;
  pnlUsd: number;
  tradesCount: number;
  winCount: number;
  lossCount: number;
  trades: any[];
  depositsSol: number;
}

interface DayTradesDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  dayData: DayDataPayload | null;
  solPrice?: number;
  onSelectMint?: (mint: string) => void;
}

function formatDuration(ms: number) {
  if (!ms || ms < 0) return "0s";
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remainingSecs = secs % 60;
  if (mins < 60) return `${mins}m ${remainingSecs}s`;
  const hrs = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  if (hrs < 24) return `${hrs}h ${remainingMins}m`;
  const days = Math.floor(hrs / 24);
  const remainingHrs = hrs % 24;
  return `${days}d ${remainingHrs}h`;
}

function truncate(str: string, max = 8) {
  if (!str) return "";
  if (str.length <= max) return str;
  return str.substring(0, 4) + "..." + str.substring(str.length - 4);
}

export function DayTradesDetailModal({
  isOpen,
  onClose,
  dayData,
  solPrice = 150,
  onSelectMint,
}: DayTradesDetailModalProps) {
  const [currencyMode, setCurrencyMode] = useState<"SOL" | "USD">("SOL");
  const [filterMode, setFilterMode] = useState<"ALL" | "WINS" | "LOSSES">("ALL");
  const [copiedMint, setCopiedMint] = useState<string | null>(null);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [isOpen, onClose]);

  const handleCopy = (mint: string) => {
    if (!mint) return;
    navigator.clipboard.writeText(mint);
    setCopiedMint(mint);
    setTimeout(() => setCopiedMint(null), 1800);
  };

  // Day KPIs
  const dayKpis = useMemo(() => {
    if (!dayData || !dayData.trades || dayData.trades.length === 0) {
      return {
        totalTrades: dayData?.tradesCount || 0,
        winCount: dayData?.winCount || 0,
        lossCount: dayData?.lossCount || 0,
        winRate: 0,
        netProfitSol: dayData?.pnlSol || 0,
        netProfitUsd: (dayData?.pnlSol || 0) * solPrice,
        profitFactor: 0,
        avgWinSol: 0,
        avgLossSol: 0,
        avgHoldDuration: 0,
        bestTrade: null,
        worstTrade: null,
        depositsSol: dayData?.depositsSol || 0,
      };
    }

    const trades = dayData.trades;
    const wins = trades.filter((t: any) => t.profitSol > 0);
    const losses = trades.filter((t: any) => t.profitSol <= 0);

    const grossProfit = wins.reduce((acc: number, t: any) => acc + t.profitSol, 0);
    const grossLoss = Math.abs(losses.reduce((acc: number, t: any) => acc + t.profitSol, 0));
    const netProfitSol = grossProfit - grossLoss;
    const netProfitUsd = netProfitSol * solPrice;

    const winRate = (wins.length / trades.length) * 100;
    const profitFactor = grossLoss > 0 ? grossProfit / grossLoss : grossProfit > 0 ? 99.9 : 0;
    const avgWinSol = wins.length > 0 ? grossProfit / wins.length : 0;
    const avgLossSol = losses.length > 0 ? grossLoss / losses.length : 0;

    const totalHoldMs = trades.reduce((sum: number, t: any) => sum + (t.duration || 0), 0);
    const avgHoldDuration = totalHoldMs / trades.length;

    const sortedByPnl = [...trades].sort((a: any, b: any) => b.profitSol - a.profitSol);
    const bestTrade = sortedByPnl[0] || null;
    const worstTrade = sortedByPnl[sortedByPnl.length - 1] || null;

    return {
      totalTrades: trades.length,
      winCount: wins.length,
      lossCount: losses.length,
      winRate,
      netProfitSol,
      netProfitUsd,
      profitFactor,
      avgWinSol,
      avgLossSol,
      avgHoldDuration,
      bestTrade,
      worstTrade,
      depositsSol: dayData.depositsSol || 0,
    };
  }, [dayData, solPrice]);

  // Filtered trades list
  const filteredTrades = useMemo(() => {
    if (!dayData || !dayData.trades) return [];
    if (filterMode === "WINS") return dayData.trades.filter((t: any) => t.profitSol > 0);
    if (filterMode === "LOSSES") return dayData.trades.filter((t: any) => t.profitSol <= 0);
    return dayData.trades;
  }, [dayData, filterMode]);

  if (!isOpen || !dayData) return null;

  const isNetProfit = dayKpis.netProfitSol >= 0;
  const formattedDate = dayData.date.toLocaleDateString(undefined, { 
    weekday: "long", 
    month: "long", 
    day: "numeric", 
    year: "numeric" 
  });

  return (
    <div 
      className="fixed inset-0 z-[10000] flex items-center justify-center p-2 sm:p-4 bg-black/85 backdrop-blur-md overflow-hidden animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-4xl max-h-[88vh] flex flex-col bg-neutral-950 border border-emerald-500/30 rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Glow styling */}
        <div className="absolute top-0 left-1/4 right-1/4 h-[1px] bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent pointer-events-none" />
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-80 h-24 bg-emerald-500/15 blur-3xl pointer-events-none rounded-full" />

        {/* ── Modal Header ─────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-black/50 shrink-0">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.2)]">
              <CalendarIcon className="size-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base sm:text-lg font-black tracking-tight text-white font-mono">
                  {formattedDate}
                </h3>
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border font-bold flex items-center gap-1 ${
                  isNetProfit 
                    ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" 
                    : "bg-rose-500/15 text-rose-400 border-rose-500/30"
                }`}>
                  {isNetProfit ? <TrendingUp className="size-2.5" /> : <TrendingDown className="size-2.5" />}
                  {isNetProfit ? "+" : ""}{currencyMode === "SOL" ? `${dayKpis.netProfitSol.toFixed(2)} SOL` : `$${dayKpis.netProfitUsd.toFixed(0)}`}
                </span>
                {dayKpis.depositsSol > 0 && (
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 font-bold flex items-center gap-1">
                    <Wallet className="size-2.5" />
                    Deposit: +{dayKpis.depositsSol.toFixed(2)} SOL
                  </span>
                )}
              </div>
              <p className="text-[11px] text-white/40 font-mono mt-0.5">
                Daily execution breakdown, token positions & behavioral statistics.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Currency toggle */}
            <div className="flex items-center bg-black/60 border border-white/10 rounded-lg p-0.5 font-mono text-[10px]">
              <button
                onClick={() => setCurrencyMode("SOL")}
                className={`px-2.5 py-1 rounded font-bold transition-all ${
                  currencyMode === "SOL" 
                    ? "bg-emerald-500 text-black shadow-sm" 
                    : "text-white/60 hover:text-white"
                }`}
              >
                SOL
              </button>
              <button
                onClick={() => setCurrencyMode("USD")}
                className={`px-2.5 py-1 rounded font-bold transition-all ${
                  currencyMode === "USD" 
                    ? "bg-emerald-500 text-black shadow-sm" 
                    : "text-white/60 hover:text-white"
                }`}
              >
                USD
              </button>
            </div>

            <button
              onClick={onClose}
              className="p-1.5 rounded-lg bg-white/5 hover:bg-rose-500/20 text-white/50 hover:text-rose-400 border border-transparent hover:border-rose-500/30 transition-colors"
              title="Close (Esc)"
            >
              <X className="size-5" />
            </button>
          </div>
        </div>

        {/* ── Scrollable Body ─────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-5 space-y-5">

          {/* ── Daily KPIs Stats Card ────────────────────────────────────── */}
          <div>
            <div className="flex items-center justify-between mb-2.5">
              <h4 className="text-[11px] font-black uppercase tracking-widest text-emerald-400 flex items-center gap-1.5">
                <BarChart3 className="size-3.5" /> Day Performance KPIs
              </h4>
              <span className="text-[10px] font-mono text-white/40">
                {dayKpis.totalTrades} Positions Traded
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2.5">
              {/* Day Net PnL */}
              <div className="bg-neutral-900/90 border border-emerald-500/20 rounded-xl p-3 flex flex-col justify-between">
                <span className="text-[9px] uppercase tracking-wider text-emerald-400/70 font-bold">Daily Net Profit</span>
                <div className="mt-1">
                  <span className={`text-base sm:text-lg font-mono font-black ${isNetProfit ? "text-emerald-400" : "text-rose-400"}`}>
                    {isNetProfit ? "+" : ""}{currencyMode === "SOL"
                      ? `${dayKpis.netProfitSol.toFixed(2)} SOL`
                      : `$${dayKpis.netProfitUsd.toFixed(0)}`}
                  </span>
                  <div className="text-[10px] font-mono text-white/40 mt-0.5">
                    {dayKpis.totalTrades} Total Trades
                  </div>
                </div>
              </div>

              {/* Day Win Rate */}
              <div className="bg-neutral-900/90 border border-white/10 rounded-xl p-3 flex flex-col justify-between">
                <span className="text-[9px] uppercase tracking-wider text-white/40 font-bold">Day Win Rate</span>
                <div className="mt-1">
                  <span className="text-base sm:text-lg font-mono font-black text-white">
                    {dayKpis.winRate.toFixed(1)}%
                  </span>
                  <div className="text-[10px] font-mono text-white/40 mt-0.5">
                    <span className="text-emerald-400 font-bold">{dayKpis.winCount}W</span> / <span className="text-rose-400 font-bold">{dayKpis.lossCount}L</span>
                  </div>
                </div>
              </div>

              {/* Profit Factor */}
              <div className="bg-neutral-900/90 border border-white/10 rounded-xl p-3 flex flex-col justify-between">
                <span className="text-[9px] uppercase tracking-wider text-white/40 font-bold">Profit Factor</span>
                <div className="mt-1">
                  <span className="text-base sm:text-lg font-mono font-black text-white">
                    {dayKpis.profitFactor.toFixed(2)}x
                  </span>
                  <div className="text-[10px] font-mono text-white/40 mt-0.5">
                    Day Ratio
                  </div>
                </div>
              </div>

              {/* Avg Win / Avg Loss */}
              <div className="bg-neutral-900/90 border border-white/10 rounded-xl p-3 flex flex-col justify-between">
                <span className="text-[9px] uppercase tracking-wider text-white/40 font-bold">Avg Win / Loss</span>
                <div className="mt-1">
                  <div className="text-xs font-mono font-bold text-emerald-400">
                    +{currencyMode === "SOL" ? `${dayKpis.avgWinSol.toFixed(2)} SOL` : `$${(dayKpis.avgWinSol * solPrice).toFixed(0)}`}
                  </div>
                  <div className="text-xs font-mono font-bold text-rose-400 mt-0.5">
                    -{currencyMode === "SOL" ? `${dayKpis.avgLossSol.toFixed(2)} SOL` : `$${(dayKpis.avgLossSol * solPrice).toFixed(0)}`}
                  </div>
                </div>
              </div>

              {/* Avg Hold Duration */}
              <div className="bg-neutral-900/90 border border-white/10 rounded-xl p-3 flex flex-col justify-between col-span-2 sm:col-span-4 lg:col-span-1">
                <span className="text-[9px] uppercase tracking-wider text-white/40 font-bold">Avg Hold Time</span>
                <div className="mt-1">
                  <div className="text-base sm:text-lg font-mono font-black text-white flex items-center gap-1">
                    <Clock className="size-3.5 text-blue-400" />
                    <span>{formatDuration(dayKpis.avgHoldDuration)}</span>
                  </div>
                  <div className="text-[10px] font-mono text-white/40 mt-0.5">
                    Per trade
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Positions Bought on this Day ──────────────────────────────── */}
          <div className="bg-neutral-900/70 border border-white/10 rounded-2xl p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-white/10">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-black text-white font-mono flex items-center gap-2">
                  <Award className="size-4 text-emerald-400" />
                  Positions Bought & Traded ({filteredTrades.length})
                </h4>
              </div>

              {/* Filter Tabs */}
              <div className="flex items-center gap-1.5 self-start sm:self-auto bg-black/40 border border-white/10 p-1 rounded-lg text-[10px] font-mono">
                <button
                  onClick={() => setFilterMode("ALL")}
                  className={`px-2.5 py-1 rounded transition-colors ${filterMode === "ALL" ? "bg-white/20 text-white font-bold" : "text-white/50 hover:text-white"}`}
                >
                  All ({dayData.tradesCount})
                </button>
                <button
                  onClick={() => setFilterMode("WINS")}
                  className={`px-2.5 py-1 rounded transition-colors ${filterMode === "WINS" ? "bg-emerald-500/20 text-emerald-400 font-bold border border-emerald-500/30" : "text-white/50 hover:text-white"}`}
                >
                  Wins ({dayData.winCount})
                </button>
                <button
                  onClick={() => setFilterMode("LOSSES")}
                  className={`px-2.5 py-1 rounded transition-colors ${filterMode === "LOSSES" ? "bg-rose-500/20 text-rose-400 font-bold border border-rose-500/30" : "text-white/50 hover:text-white"}`}
                >
                  Losses ({dayData.lossCount})
                </button>
              </div>
            </div>

            {/* Positions List */}
            <div className="mt-3 divide-y divide-white/5 max-h-[50vh] overflow-y-auto custom-scrollbar">
              {filteredTrades.length === 0 ? (
                <div className="text-center py-10 text-xs text-white/40 font-mono">
                  No positions match the selected filter on this day.
                </div>
              ) : (
                filteredTrades.map((t: any, idx: number) => {
                  const isProfit = t.profitSol > 0;
                  const meta = t.tokenMeta || {};
                  const isCopied = copiedMint === t.mint;
                  return (
                    <div 
                      key={idx} 
                      className="py-3 px-2.5 hover:bg-white/[0.02] rounded-xl transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-[11px] font-mono"
                    >
                      {/* Left: Token info */}
                      <div className="flex items-center gap-3 min-w-[200px]">
                        <TokenAvatar 
                          mint={t.mint} 
                          symbol={meta.symbol} 
                          name={meta.name} 
                          imageUri={meta.imageUri || meta.image} 
                          size="size-9" 
                        />
                        <div className="flex flex-col min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-bold text-white text-xs truncate max-w-[130px]">
                              {meta.symbol || meta.name || truncate(t.mint, 4)}
                            </span>
                            {t.openReason && (
                              <span className="text-[8px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 truncate max-w-[110px]">
                                {t.openReason}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5 text-[9px] text-white/40">
                            <button
                              onClick={() => handleCopy(t.mint)}
                              className="flex items-center gap-1 hover:text-white transition-colors cursor-pointer"
                              title="Copy token address"
                            >
                              <span>{truncate(t.mint, 4)}</span>
                              {isCopied ? <Check className="size-2 text-emerald-400" /> : <Copy className="size-2" />}
                            </button>
                            <span>•</span>
                            <span>{new Date(t.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                            <span>•</span>
                            <span className="flex items-center gap-0.5 text-blue-400">
                              <Clock className="size-2.5" />
                              {formatDuration(t.duration)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Middle: Execution Metrics */}
                      <div className="flex items-center gap-2 text-white/70 text-[10px] bg-white/[0.02] px-2.5 py-1.5 rounded-lg border border-white/5">
                        <div className="flex flex-col">
                          <span className="text-[8px] text-white/40 uppercase">Market Cap</span>
                          <span>{formatUsdCompact(t.entryMcUsd)} → <strong className="text-white">{formatUsdCompact(t.exitMcUsd)}</strong></span>
                        </div>
                        {t.exitReason && (
                          <div className="hidden md:flex flex-col border-l border-white/5 pl-2 ml-2">
                            <span className="text-[8px] text-white/40 uppercase">Exit</span>
                            <span className="text-white/80 truncate max-w-[100px]">{t.exitReason}</span>
                          </div>
                        )}
                      </div>

                      {/* Right: Realized PnL & Chart action */}
                      <div className="flex items-center gap-3 shrink-0 self-end sm:self-auto ml-auto sm:ml-0">
                        <div className="text-right">
                          <div className={`font-black text-sm flex items-center justify-end gap-1 ${isProfit ? "text-emerald-400" : "text-rose-400"}`}>
                            {isProfit ? <ArrowUpRight className="size-3.5" /> : <ArrowDownRight className="size-3.5" />}
                            <span>{isProfit ? "+" : ""}{currencyMode === "SOL" ? `${t.profitSol.toFixed(2)} SOL` : `$${t.pnlUsd.toFixed(0)}`}</span>
                          </div>
                          <div className={`text-[10px] font-bold ${isProfit ? "text-emerald-400/80" : "text-rose-400/80"}`}>
                            {isProfit ? "+" : ""}{(t.pnlPct * 100).toFixed(1)}%
                          </div>
                        </div>

                        {onSelectMint && (
                          <button
                            onClick={() => {
                              onSelectMint(t.mint);
                              onClose();
                            }}
                            className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/25 transition-all text-[10px] font-bold"
                            title="Focus in Chart"
                          >
                            <ExternalLink className="size-3" />
                            <span className="hidden sm:inline">Chart</span>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
